import AsyncStorage from '@react-native-async-storage/async-storage';
import { createMMKV } from 'react-native-mmkv';

import { noCircularLogger } from 'utils/logger/no-circular-logger';

/** MMKV storage instance, used for fast/sync storage. Please use the public API below for most cases. */
export const _mmkvStorage = createMMKV();

// values can't be set to null
const handleSet = (key: string, value: string | boolean | number | null | undefined) => {
  if (value === null || value === undefined) {
    _mmkvStorage.remove(key);
  } else {
    _mmkvStorage.set(key, value);
  }
};

/** MMKV backed fast SYNC storage */
export const syncStorage = {
  getObject: <V = any>(key: ObjectSyncStorageKeys): V | null => {
    let value: any;
    try {
      value = _mmkvStorage.getString(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      noCircularLogger('error', {
        message: 'syncStorage failed to parse JSON',
        context: { key, value },
        error,
      });
      return null;
    }
  },
  setObject: <V = any>(key: ObjectSyncStorageKeys, value: V) => {
    try {
      handleSet(key, JSON.stringify(value));
    } catch (error) {
      noCircularLogger('error', {
        message: 'syncStorage failed to stringify JSON',
        context: { key },
        error,
      });
    }
  },

  getString: (key: StringSyncStorageKeys) => _mmkvStorage.getString(key),
  setString: (key: StringSyncStorageKeys, value: string) => handleSet(key, value),

  getBoolean: (key: BooleanSyncStorageKeys) => _mmkvStorage.getBoolean(key),
  setBoolean: (key: BooleanSyncStorageKeys, value: boolean) => handleSet(key, value),

  getNumber: (key: NumberSyncStorageKeys) => _mmkvStorage.getNumber(key),
  setNumber: (key: NumberSyncStorageKeys, value: number) => handleSet(key, value),

  remove: (key: SyncStorageKey) => _mmkvStorage.remove(key),

  clearAll: () => _mmkvStorage.clearAll(),
  clearAllExcept: (exceptKeys: SyncStorageKey[]) => {
    _mmkvStorage.getAllKeys().forEach(key => {
      if (!key.startsWith('RBI::')) {
        // only our keys, just in case a 3rd party library is using MMKV
        return;
      }
      if (!exceptKeys.includes(key as SyncStorageKey)) {
        _mmkvStorage.remove(key);
      }
    });
  },

  getObjectWithExpiration: <V = any>(key: ObjectSyncStorageKeys): V | null => {
    const value = syncStorage.getObject(key);
    if (!value) {
      return null;
    }

    const now = new Date().getTime();
    if (now < value.expirationTime) {
      // Item is still valid, return the value
      return value.value;
    }
    // Item has expired, remove it from local storage
    syncStorage.remove(key);
    return null;
  },
  setObjectWithExpiration: <V = any>(key: ObjectSyncStorageKeys, value: V, expMinutes: number) => {
    const now = new Date();
    const expirationTime = now.getTime() + expMinutes * 60 * 1000; // Convert minutes to milliseconds

    return syncStorage.setObject(key, {
      value,
      expirationTime,
    });
  },
};

export enum SyncStorageKey {
  APPLIED_LOYALTY_OFFERS = 'RBI::APPLIED_LOYALTY_OFFERS',
  APPLIED_LOYALTY_REWARDS = 'RBI::APPLIED_LOYALTY_REWARDS',
  AUTH_REDIRECT = 'RBI::AUTH_REDIRECT',
  AUTO_SELECT_STORE_TIMESTAMP = 'RBI::AUTO_SELECT_TIMESTAMP',
  CAMPAIGNS_LOCAL_STORAGE = 'RBI::CAMPAIGNS_LOCAL_STORAGE',
  CART = 'RBI::CART',
  COMPLETED_USER_JOURNEY_FIELDS = 'RBI::COMPLETED_USER_JOURNEY_FIELDS',
  COMPLETED_USER_JOURNEY_SEEN = 'RBI::COMPLETED_USER_JOURNEY_SEEN',
  DEBUG_CHAOS_MODE = 'RBI::DEBUG_CHAOS_MODE',
  DELIVERY_SURCHARGE_FEE = 'RBI::DELIVERY_SURCHARGE_FEE',
  DEVICE_ID = 'RBI::DEVICE_ID',
  HAS_MIGRATED_FROM_CAPACITOR_STORAGE = 'RBI::HAS_MIGRATED_FROM_CAPACITOR_STORAGE',
  HAS_MIGRATED_FROM_ASYNC_STORAGE = 'RBI::HAS_MIGRATED_FROM_ASYNC_STORAGE',
  IN_RESTAURANT_CART_LAST_UPDATE = 'RBI::IN_RESTAURANT_CART_LAST_UPDATE',
  IN_RESTAURANT_REDEMPTION = 'RBI::LOYALTY_IN_RESTAURANT_REDEMPTION',
  IN_RESTAURANT_REDEMPTION_SHORT_CODE = 'RBI::LOYALTY_IN_RESTAURANT_REDEMPTION_SHORT_CODE',
  INTERNAL_BUILD_ONLY_ENV_PICKER_CONFIG = 'RBI::INTERNAL_BUILD_ONLY_ENV_PICKER_CONFIG',
  IS_ORIENTATION_DEFAULT = 'RBI::IS_ORIENTATION_DEFAULT',
  LANGUAGE = 'RBI::LANGUAGE',
  LAST_CRASH_KEY_CLEAR_TIME = 'RBI::LAST_CRASH_KEY_CLEAR_TIME',
  LAST_ERROR_BOUNDARY_CRASH_TIME = 'RBI::ERROR_BOUNDARY_CRASHES',
  LAST_SEEN_LOYALTY_POINTS = 'RBI::LAST_SEEN_LOYALTY_POINTS',
  LAST_TIME_STORE_UPDATED = 'RBI::LAST_TIME_STORE_UPDATED',
  LAST_VERSION_INFO = 'RBI::LAST_VERSION_INFO',
  LAUNCH_DARKLY_USER_ATTRIBUTES = 'RBI::LAUNCH_DARKLY_USER_ATTRIBUTES',
  LOCATION_CAN_ASK_AGAIN = 'RBI::LOCATION_CAN_ASK_AGAIN',
  LOGIN_EMAIL = 'RBI::LOGIN',
  LOYALTY_ONBOARDING_SEEN = 'RBI::LOYALTY_ONBOARDING_SEEN',
  MARKETING_EMAIL_SUBSCRIPTION_REQUESTED = 'RBI::MARKETING_EMAIL_SUBSCRIPTION_REQUESTED',
  MARKETING_PUSH_SUBSCRIPTION_REQUESTED = 'RBI::MARKETING_PUSH_SUBSCRIPTION_REQUESTED',
  MARKETING_SMS_SUBSCRIPTION_REQUESTED = 'RBI::MARKETING_SMS_SUBSCRIPTION_REQUESTED',
  MINIMAL_CART = 'RBI::MINIMAL_CART',
  ONBOARDING_DATA_SENT = 'RBI::ONBOARDING_DATA_SENT',
  ONBOARDING_STATE = 'RBI::ONBOARDING_STATE',
  ORDER = 'RBI::ORDER',
  ORDER_ID = 'RBI::ORDER_ID',
  ORDER_LAST_UPDATE = 'RBI::ORDER_LAST_UPDATE',
  OTP = 'RBI::OTP',
  PRICE_ORDER_INPUT = 'RBI::PRICE_ORDER_INPUT',
  READ_CONTENT_CARDS = 'RBI::READ_CONTENT_CARDS',
  REDEEM_LAST_CODE_STATE = 'RBI::REDEEM_LAST_CODE_STATE',
  REDEEM_SHOULD_REFRESH = 'RBI::REDEEM_SHOULD_REFRESH',
  SELECTED_LOYALTY_OFFER = 'RBI::SELECTED_LOYALTY_OFFER',
  SELECTED_STORE_LOCAL_TIME = 'RBI::SELECTED_STORE_LOCAL_TIME',
  SERVICE_MODE = 'RBI::SERVICE_MODE',
  SKIP_AUTO_SELECT_PROMPT = 'RBI::SKIP_AUTO_SELECT_PROMPT',
  SOCIAL_LOGIN_USER_EMAIL = 'RBI::SOCIAL_LOGIN_USER_EMAIL',
  SOCIAL_LOGIN_USER_NAME = 'RBI::SOCIAL_LOGIN_USER_NAME',
  STORE = 'RBI::STORE',
  TEMP_FUTURE_ORDERING_TIME = 'RBI::TEMP_FUTURE_ORDERING_TIME',
  USER_ID = 'RBI::USER_ID',
  USER_SIGNED_IN_SUCCESSFULLY = 'RBI::USER_SIGNED_IN_SUCCESSFULLY',
  VEHICLE_DESCRIPTION = 'RBI::VEHICLE_DESCRIPTION',
}

type ObjectSyncStorageKeys =
  | SyncStorageKey.APPLIED_LOYALTY_OFFERS
  | SyncStorageKey.APPLIED_LOYALTY_REWARDS
  | SyncStorageKey.AUTH_REDIRECT
  | SyncStorageKey.CAMPAIGNS_LOCAL_STORAGE
  | SyncStorageKey.CART
  | SyncStorageKey.COMPLETED_USER_JOURNEY_FIELDS
  | SyncStorageKey.COMPLETED_USER_JOURNEY_SEEN
  | SyncStorageKey.IN_RESTAURANT_REDEMPTION
  | SyncStorageKey.IN_RESTAURANT_REDEMPTION_SHORT_CODE
  | SyncStorageKey.INTERNAL_BUILD_ONLY_ENV_PICKER_CONFIG
  | SyncStorageKey.LAST_SEEN_LOYALTY_POINTS
  | SyncStorageKey.LAST_VERSION_INFO
  | SyncStorageKey.LAUNCH_DARKLY_USER_ATTRIBUTES
  | SyncStorageKey.LOYALTY_ONBOARDING_SEEN
  | SyncStorageKey.MARKETING_EMAIL_SUBSCRIPTION_REQUESTED
  | SyncStorageKey.MARKETING_PUSH_SUBSCRIPTION_REQUESTED
  | SyncStorageKey.MARKETING_SMS_SUBSCRIPTION_REQUESTED
  | SyncStorageKey.MINIMAL_CART
  | SyncStorageKey.ONBOARDING_STATE
  | SyncStorageKey.ORDER
  | SyncStorageKey.ORDER_ID
  | SyncStorageKey.OTP
  | SyncStorageKey.PRICE_ORDER_INPUT
  | SyncStorageKey.READ_CONTENT_CARDS
  | SyncStorageKey.REDEEM_LAST_CODE_STATE
  | SyncStorageKey.SELECTED_LOYALTY_OFFER
  | SyncStorageKey.SERVICE_MODE
  | SyncStorageKey.STORE;

type StringSyncStorageKeys =
  | SyncStorageKey.DEBUG_CHAOS_MODE
  | SyncStorageKey.DEVICE_ID
  | SyncStorageKey.IN_RESTAURANT_CART_LAST_UPDATE
  | SyncStorageKey.LANGUAGE
  | SyncStorageKey.LOGIN_EMAIL
  | SyncStorageKey.ORDER_LAST_UPDATE
  | SyncStorageKey.SELECTED_STORE_LOCAL_TIME
  | SyncStorageKey.SOCIAL_LOGIN_USER_EMAIL
  | SyncStorageKey.SOCIAL_LOGIN_USER_NAME
  | SyncStorageKey.TEMP_FUTURE_ORDERING_TIME
  | SyncStorageKey.USER_ID
  | SyncStorageKey.VEHICLE_DESCRIPTION;

type BooleanSyncStorageKeys =
  | SyncStorageKey.HAS_MIGRATED_FROM_ASYNC_STORAGE
  | SyncStorageKey.HAS_MIGRATED_FROM_CAPACITOR_STORAGE
  | SyncStorageKey.IS_ORIENTATION_DEFAULT
  | SyncStorageKey.LOCATION_CAN_ASK_AGAIN
  | SyncStorageKey.ONBOARDING_DATA_SENT
  | SyncStorageKey.REDEEM_SHOULD_REFRESH
  | SyncStorageKey.SKIP_AUTO_SELECT_PROMPT
  | SyncStorageKey.USER_SIGNED_IN_SUCCESSFULLY;

type NumberSyncStorageKeys =
  | SyncStorageKey.AUTO_SELECT_STORE_TIMESTAMP
  | SyncStorageKey.DELIVERY_SURCHARGE_FEE
  | SyncStorageKey.LAST_CRASH_KEY_CLEAR_TIME
  | SyncStorageKey.LAST_ERROR_BOUNDARY_CRASH_TIME
  | SyncStorageKey.LAST_TIME_STORE_UPDATED;

let didErrorClear = false;

// todo remove after 3/15/2026 - non critical values (no auth) is stored in AsyncStorage
export async function TEMP_asyncToSyncStorageMigration(): Promise<void> {
  const hasMigratedFromAsyncStorage = syncStorage.getBoolean(
    SyncStorageKey.HAS_MIGRATED_FROM_ASYNC_STORAGE
  );

  if (hasMigratedFromAsyncStorage === true) {
    // no longer need for AsyncStorage
    return Promise.resolve();
  }

  try {
    // only extract values we own
    const values = await AsyncStorage.multiGet(Object.values(SyncStorageKey));

    values.forEach(([key, value]: [string, string | null]) => {
      if (value === null || value === undefined) {
        return;
      }

      try {
        if (value === 'true' || value === 'false') {
          // update legacy boolean strings to booleans
          _mmkvStorage.set(key, value === 'true');
        } else if (value.startsWith('"')) {
          // Handle potentially multiple levels of JSON encoding
          // e.g., '"{...}"' (double), '"\\"...\\""' (triple), etc.
          try {
            let parsedValue: any = value;
            let parseCount = 0;
            const maxParseAttempts = 5; // Prevent infinite loops

            // Keep parsing until we get a non-string value or a string that doesn't start with a quote
            while (
              typeof parsedValue === 'string' &&
              parsedValue.startsWith('"') &&
              parseCount < maxParseAttempts
            ) {
              parsedValue = JSON.parse(parsedValue);
              parseCount++;
            }

            _mmkvStorage.set(key, parsedValue);
          } catch (error) {
            // If JSON parsing fails, fall back to simple string unwrapping
            noCircularLogger('warn', {
              isMmkvMigration: true,
              message:
                'TEMP_asyncToSyncStorageMigration - failed to parse multi-encoded JSON, using fallback',
              context: { key, value },
              error,
            });
            // Fallback: just remove outer quotes
            _mmkvStorage.set(key, value.slice(1, -1));
          }
        } else if (numberRegex.test(value)) {
          // number as a string to number
          try {
            _mmkvStorage.set(key, parseFloat(value));
          } catch (error) {
            noCircularLogger('warn', {
              isMmkvMigration: true,
              message:
                'TEMP_asyncToSyncStorageMigration - failed to convert number string to number',
              context: { key },
              error,
            });
          }
        } else {
          // objects will be still a string but read as an object in updated code
          _mmkvStorage.set(key, value);
        }

        AsyncStorage.removeItem(key);
      } catch (error) {
        noCircularLogger('warn', {
          isMmkvMigration: true,
          message: 'TEMP_asyncToSyncStorageMigration - unknown error',
          context: { key },
          error,
        });
      }
    });

    syncStorage.setBoolean(SyncStorageKey.HAS_MIGRATED_FROM_ASYNC_STORAGE, true);
    noCircularLogger('info', {
      isMmkvMigration: true,
      message: 'TEMP_asyncToSyncStorageMigration completed successfully',
    });
  } catch (error) {
    // ANDROID ONLY: "Row too big to fit into CursorWindow" error can happen on >2MB key value. The app will empty most keys except login so there is no crash and the app can continue
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isCursorWindowError = errorMessage.includes('CursorWindow');

    noCircularLogger('error', {
      message: `[RN LocalStorage] migration failed - ${
        isCursorWindowError
          ? 'A stored value likely exceeds Android 2MB CursorWindow limit'
          : 'Unknown AsyncStorage error'
      } - ${
        didErrorClear ? 'Not clearing non user keys' : 'Clearing non user keys and re-reading'
      }`,
      error,
      didErrorClear,
      isCursorWindowError,
    });

    if (!didErrorClear && isCursorWindowError) {
      didErrorClear = true;
      // clear non user keys to prevent the error caused by too large a value on next launch
      await TEMP_asyncToSyncStorageMigration(); // re-sync non cleared keys
    }
  }
}

const numberRegex = /^-?\d*\.?\d+$/;
